/**
 * PC↔H5 设备跳转 — 灰度控制
 *
 * 同步维护：public/device-redirect-gray.boot.js
 *
 * 测试 URL（可选，非白名单联调才需要）：
 * - `?device_redirect=1` 强制命中（当次会话持久，便于联调）
 * - `?device_redirect=0` / `?stay=1` / `?no_device_redirect=1` 强制关闭
 */

/** 总开关：false = 全站关闭跳转 */
export const DEVICE_REDIRECT_MASTER_ENABLED = true

/** true = 仅灰度命中用户；false = 全量（须 MASTER 为 true） */
export const DEVICE_REDIRECT_GRAY_MODE = false

/** 稳定分桶比例 0–100（0 = 仅白名单；勿在未放量前调大） */
export const DEVICE_REDIRECT_GRAY_PERCENT = 0

/** 登录用户 ID 白名单（localStorage userInfo.id；仅名单内用户跳转，其余完全不受影响） */
export const DEVICE_REDIRECT_GRAY_USER_IDS = ['55', '4727', '5602', '508', '2765', '7018', '549']

const BUCKET_STORAGE_KEY = 'cibeiai_device_redirect_gray_bucket'
const FORCE_SESSION_KEY = 'cibeiai_device_redirect_force'

function parseSearch(search = '') {
  try {
    return new URLSearchParams(String(search).replace(/^\?/, ''))
  } catch (e) {
    return new URLSearchParams()
  }
}

/** @returns {true|false|null} null = 未指定 */
export function parseRedirectQueryOverride(search = '') {
  const q = parseSearch(search)
  if (q.get('stay') === '1' || q.get('no_device_redirect') === '1') return false
  if (q.get('device_redirect') === '0') return false
  if (q.get('device_redirect') === '1') return true
  return null
}

export function captureRedirectQueryOverride(search = '') {
  if (typeof sessionStorage === 'undefined') return
  const override = parseRedirectQueryOverride(search)
  try {
    if (override === true) sessionStorage.setItem(FORCE_SESSION_KEY, '1')
    if (override === false) sessionStorage.removeItem(FORCE_SESSION_KEY)
  } catch (e) {}
}

export function getStableGrayBucket() {
  if (typeof localStorage === 'undefined') return 100
  try {
    const raw = localStorage.getItem(BUCKET_STORAGE_KEY)
    if (raw != null && raw !== '') {
      const n = Number(raw)
      if (Number.isFinite(n) && n >= 0 && n < 100) return Math.floor(n)
    }
    const next = Math.floor(Math.random() * 100)
    localStorage.setItem(BUCKET_STORAGE_KEY, String(next))
    return next
  } catch (e) {
    return 100
  }
}

export function getLoggedInUserIdForGray() {
  if (typeof localStorage === 'undefined') return ''
  try {
    const raw = localStorage.getItem('userInfo')
    if (!raw) return ''
    const user = JSON.parse(raw)
    const id = user?.id ?? user?.user_id ?? user?.member_id
    return id != null && String(id).trim() ? String(id).trim() : ''
  } catch (e) {
    return ''
  }
}

function isUserInGrayWhitelist(userId) {
  if (!userId || !DEVICE_REDIRECT_GRAY_USER_IDS.length) return false
  const set = new Set(DEVICE_REDIRECT_GRAY_USER_IDS.map((id) => String(id).trim()).filter(Boolean))
  return set.has(userId)
}

function hasSessionForceOn() {
  if (typeof sessionStorage === 'undefined') return false
  try {
    return sessionStorage.getItem(FORCE_SESSION_KEY) === '1'
  } catch (e) {
    return false
  }
}

/** 当前访问是否应执行 PC↔H5 设备跳转 */
export function isDeviceRedirectEnabled(options = {}) {
  const search = options.search ?? (typeof window !== 'undefined' ? window.location.search : '')

  captureRedirectQueryOverride(search)

  const override = parseRedirectQueryOverride(search)
  if (override === false) return false
  if (override === true) return true

  if (!DEVICE_REDIRECT_MASTER_ENABLED) return false
  if (!DEVICE_REDIRECT_GRAY_MODE) return true

  if (hasSessionForceOn()) return true

  const userId = getLoggedInUserIdForGray()
  if (isUserInGrayWhitelist(userId)) return true

  const percent = Math.max(0, Math.min(100, Number(DEVICE_REDIRECT_GRAY_PERCENT) || 0))
  if (percent <= 0) return false

  return getStableGrayBucket() < percent
}
